import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Tabs,
  Tab,
  CardContent,
  Typography,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert
} from '@mui/material';
import { StyledCard, SectionTitle } from '@Pages/RegulatoryMember/ExitRequestDetail/styles';
import {
  PageContainer,
  HeaderContainer,
  cardTransition,
  cardContentStyles,
  sectionTitleStyles
} from '@Pages/RnDMember/ExitRequestResponseUnified/ExitRequestResponseStyles';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import EditCalendarRoundedIcon from '@mui/icons-material/EditCalendarRounded';
import PageTitle from '@Components/PageTitle/PageTitle';
import LoadingComponent from "@Components/LoadingComponent/LoadingComponent";
import { DeletePopup } from "@Components/Popup/DeletePopup";
import { useDispatch, useSelector } from "react-redux";
import { setSnackbar } from "@Redux/snackBarSlice";
import themeCreator from '@Themes/themeCreator';
import axiosInstance from "../../../../API/axios";

// Local imports
import { useExitPlanData } from './useExitPlanData';
import { RequestDetailsTab } from '../Tabs/RequestDetailsTab/RequestDetailsTab';
import { SupportRequestTab } from '../Tabs/Support/SupportRequestTab/SupportRequestTab';
import { ExitPlanFormSection } from '../ExitPlanFormSection/ExitPlanFormSection';
import { AffectedProductsTab } from '../Tabs/AffectedProductsTab/AffectedProductsTab';
import { SupportDetailsTab } from "../Tabs/Support/SupportDetailsTab/SupportDetailsTab";
import { PlanStatusTab } from "../Tabs/PlanStatusTab/PlanStatusTab";
import SupportTab from "../Tabs/Support/SupportTab";
import RequestApproverDialog from "@Components/RequestApproverDialog/RequestApproverDialog";
import ExtensionRequestDialog from "@Components/ExtensionRequestDialog/ExtensionRequestDialog";
import ExtensionStatusBanner from "@Components/ExtensionRequestDialog/ExtensionStatusBanner";

// Component imports
import { PlanChipsSection } from './PlanChipsSection';
import { PlanProductsDisplay } from './PlanProductsDisplay';
import { PlanViewContent } from './PlanViewContent';

// Constants and utilities
import {
  TAB_INDICES,
  VIEW_TAB_INDICES,
  TAB_LABELS,
  UI_TEXT,
  DEFAULT_SUPPORT_STATE,
  USER_ROLES
} from './ExitPlanPage.constants';
import {
  isSupportValid,
  isSupportTeamSelectedButNoMember,
  getNextPlanNumber,
  mapBackendPlanToFrontend,
  prepareSupportUserIds
} from './ExitPlanPage.utils';
import {
  ModeSwitch,
  contentContainerStyles,
  leftColumnStyles,
  rightColumnStyles,
  tabsStyles,
  tabContentStyles,
  rightCardContentStyles,
  stickyTitleStyles,
  emptyStateStyles
} from './ExitPlanPage.styles';
import { PLAN_TYPES, normalizePlanType, isExemption, EMPTY_EXEMPTION_FIELDS } from '@Utils/planType';

const ExitPlanPage = ({ initialPlanType = PLAN_TYPES.EXIT_PLAN }) => {
  // ================ Hooks & Navigation ================
  const { planId, reqId, categoryAssignmentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = themeCreator();
  const userId = useSelector((state) => state.user.userId);

  // ================ Initial State Setup ================
  const isEditing = Boolean(planId);
  const skipLoading = Boolean(location.state?.skipLoading);

  // ================ Loading States ================
  const [showLoading, setShowLoading] = useState(!skipLoading);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [extensionDialogOpen, setExtensionDialogOpen] = useState(false);

  // ================ Custom Hook Data ================
  const {
    loading,
    requestData,
    formErrors,
    exitDate,
    endOfProductionDate,
    endOfImportationDate,
    replacementPlan,
    liquidationPlan,
    additionalComments,
    setExitDate,
    setEndOfProductionDate,
    setEndOfImportationDate,
    setReplacementPlan,
    setLiquidationPlan,
    setAdditionalComments,
    planType,
    setPlanType,
    toImpactEuros,
    exceptionStartDate,
    exceptionEndDate,
    justification,
    safetyAssessmentReference,
    productTypes,
    brands,
    setToImpactEuros,
    setExceptionStartDate,
    setExceptionEndDate,
    setJustification,
    setSafetyAssessmentReference,
    setProductTypes,
    setBrands,
    setFormErrors,
    validateForm,
    createExitPlan,
    deleteExitPlan,
    submitExitPlan,
    submitLoading,
    readOnly,
    isApprovingManager, // <-- get from hook
    canEdit,
  } = useExitPlanData(isEditing, { initialPlanType });

  // ================ Tab States ================
  const [tabValue, setTabValue] = useState(0);
  const [viewTabValue, setViewTabValue] = useState(0);
  const [editLeftTab, setEditLeftTab] = useState(0);

  // ================ Plan Management States ================
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [createdPlans, setCreatedPlans] = useState([]);
  const [plannedProductIds, setPlannedProductIds] = useState([]);
  const [activePlanId, setActivePlanId] = useState(null);
  const [editSelectedProductIds, setEditSelectedProductIds] = useState([]);
  const [planViewMode, setPlanViewMode] = useState(true);

  // ================ Snapshot States (for dirty checking) ================
  const [planProductIdsSnapshot, setPlanProductIdsSnapshot] = useState([]);
  const [planFormSnapshot, setPlanFormSnapshot] = useState(null);
  const [isPlanDirty, setIsPlanDirty] = useState(false);

  // ================ Support State ================
  const [supportState, setSupportState] = useState(DEFAULT_SUPPORT_STATE);

  // Add state for reminder dialog and plan creation flag
  const [showUnplannedProductsDialog, setShowUnplannedProductsDialog] = useState(false);
  const [planJustCreated, setPlanJustCreated] = useState(false);
  const [approvalType, setApprovalType] = useState("approve"); // "approve" or "reject"
  const [approvalComment, setApprovalComment] = useState("");
  const [requestApproverOpen, setRequestApproverOpen] = useState(false);


  const isReadOnly = readOnly || !canEdit
  // ================ Helper Functions ================
  const getPlanById = (planId) => createdPlans.find((p) => p.id === planId);

  // Add this function to initialize form fields from a plan
  const initializeFormFromPlan = (plan) => {
    if (!plan) return;
    const d = plan.formData;
    setPlanType(normalizePlanType(d.planType));
    setToImpactEuros(d.toImpactEuros ?? EMPTY_EXEMPTION_FIELDS.toImpactEuros);
    setExceptionStartDate(d.exceptionStartDate || null);
    setExceptionEndDate(d.exceptionEndDate || null);
    setJustification(d.justification || '');
    setSafetyAssessmentReference(d.safetyAssessmentReference || '');
    setProductTypes(d.productTypes || '');
    setBrands(d.brands || '');
    setExitDate(d.exitDate || null);
    setEndOfProductionDate(d.endOfProductionDate || null);
    setEndOfImportationDate(d.endOfImportationDate || null);
    setReplacementPlan(d.replacementPlan || '');
    setLiquidationPlan(d.liquidationPlan || '');
    setAdditionalComments(d.additionalComments || '');
    setFormErrors({});
    setPlanProductIdsSnapshot(plan.productIds);
    setPlanFormSnapshot({ ...d });
    setEditSelectedProductIds(plan.productIds);
    setSupportState({
      needsSupport: d.needsSupport || false,
      marketingSupport: d.marketingSupport || false,
      supplyChainSupport: d.supplyChainSupport || false,
      selectedMarketingMember: d.selectedMarketingMember || null,
      selectedSupplyChainMember: d.selectedSupplyChainMember || null
    });
  };

  const getUnplannedProducts = () =>
      requestData?.products.filter((p) =>
      !plannedProductIds.includes(p.productId) && p.isAffected === true
      ) || [];

  const getActivePlanProducts = () => {
    if (!activePlanId) return [];
    return requestData?.products.filter((p) => editSelectedProductIds.includes(p.productId));
  };

  const getActivePlanDetails = () => {
    const plan = getPlanById(activePlanId);
    return plan ? plan.formData : {};
  };

  const getActivePlanBackendData = () => {
    if (!activePlanId || !requestData?.exitPlans) return null;
    console.log("request data ",requestData);
    console.log("active plan id ",activePlanId);
    return requestData.exitPlans.find(plan => plan.id === activePlanId) || null;
  };

  const activeCategoryAssignmentId = categoryAssignmentId || requestData?.categoryAssignmentId;
  const activeExitRequestId = reqId || requestData?.exitRequest?.id;

  const getEditablePlanProducts = () => {
    if (!activePlanId) return [];
    const allProducts = requestData?.products || [];
    const otherPlanProductIds = createdPlans
        .filter((plan) => plan.id !== activePlanId)
        .flatMap((plan) => plan.productIds);
    return allProducts.filter(
        (p) =>
            (editSelectedProductIds.includes(p.productId) ||
                !otherPlanProductIds.includes(p.productId)) &&
            p.isAffected === true
    );
  };

  // ================ Support State Setters ================
  const setPlanNeedsSupport = (val) => setSupportState((s) => ({ ...s, needsSupport: val }));
  const setPlanMarketingSupport = (val) => setSupportState((s) => ({ ...s, marketingSupport: val }));
  const setPlanSupplyChainSupport = (val) => setSupportState((s) => ({ ...s, supplyChainSupport: val }));
  const setPlanSelectedMarketingMember = (val) => setSupportState((s) => ({ ...s, selectedMarketingMember: val }));
  const setPlanSelectedSupplyChainMember = (val) => setSupportState((s) => ({ ...s, selectedSupplyChainMember: val }));

  // ================ Event Handlers ================
  const handleBack = () => navigate(-1);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 2) setFormErrors(prev => ({ ...prev, supportTeams: undefined }));
  };

  const handleSelectProduct = (productId) => {
    setSelectedProductIds((prev) =>
        prev.includes(productId)
            ? prev.filter((id) => id !== productId)
            : [...prev, productId]
    );
  };

  const handleRemoveProductFromPlan = (productId) => {
    if (!activePlanId) return;
    setEditSelectedProductIds((prev) => prev.filter((id) => id !== productId));
  };

  const handlePlanNameClick = (planId) => {
    if (activePlanId === planId && !isReadOnly) {
      handleAddAnotherPlan();
      return;
    }
    const plan = getPlanById(planId);
    if (plan) {
      initializeFormFromPlan(plan);
    }
    setActivePlanId(planId);
    setEditLeftTab(1);
    setPlanViewMode(true);
  };

  const handleAddAnotherPlan = () => {
    setActivePlanId(null);
    setFormErrors({});
    setExitDate(null);
    setEndOfProductionDate(null);
    setEndOfImportationDate(null);
    setReplacementPlan('');
    setLiquidationPlan('');
    setAdditionalComments('');
    setPlanType(normalizePlanType(initialPlanType));
    setToImpactEuros(EMPTY_EXEMPTION_FIELDS.toImpactEuros);
    setExceptionStartDate(EMPTY_EXEMPTION_FIELDS.exceptionStartDate);
    setExceptionEndDate(EMPTY_EXEMPTION_FIELDS.exceptionEndDate);
    setJustification(EMPTY_EXEMPTION_FIELDS.justification);
    setSafetyAssessmentReference(EMPTY_EXEMPTION_FIELDS.safetyAssessmentReference);
    setProductTypes(EMPTY_EXEMPTION_FIELDS.productTypes);
    setBrands(EMPTY_EXEMPTION_FIELDS.brands);
    setSelectedProductIds([]);
    setPlanProductIdsSnapshot([]);
    setPlanFormSnapshot(null);
    setEditSelectedProductIds([]);
    setSupportState(DEFAULT_SUPPORT_STATE);
  };

  const handleCreatePlan = async () => {
    if (!validateForm()) {
      console.log("Form validation failed, not submitting.");
      return;
    }

    setSubmitting(true);
    const supportUserIds = prepareSupportUserIds(supportState);
    const products = requestData?.products
        .filter((p) => selectedProductIds.includes(p.productId))
        .map(({ id, ...rest }) => rest);

    try {
      const res = await createExitPlan({
        exitRequestResponseId: requestData.id,
        additionalComments,
        exitDate,
        replacementPlan,
        endOfProductionDate,
        endOfImportationDate,
        liquidationPlan,
        planType,
        toImpactEuros,
        exceptionStartDate,
        exceptionEndDate,
        justification,
        safetyAssessmentReference,
        productTypes,
        brands,
        marketingSupport: supportState.marketingSupport,
        supplyChainSupport: supportState.supplyChainSupport,
        supportUserIds,
        products
      });

      // Always use backend-provided planId
      const planId = res.id;
      console.log("plan created with id: ", planId);
      const planNumber = getNextPlanNumber(createdPlans);
      const planName = `Plan #${planNumber}`;
      const mapped = mapBackendPlanToFrontend(res, createdPlans.length);
      setCreatedPlans((prev) => [
        ...prev,
        { id: planId, planType: mapped.planType, name: planName, productIds: selectedProductIds, formData: mapped.formData }
      ]);
      setPlannedProductIds((prev) => [...prev, ...selectedProductIds]);
      setActivePlanId(planId); // Set activePlanId to backend ID
        setPlanJustCreated(true);
      // Reset form
      handleAddAnotherPlan();
      setTabValue(1);

    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdatePlan = async () => {
    if (!validateForm()) {
      console.log("Form validation failed, not submitting.");
      return;
    }

    setSubmitting(true);
    try {
      const supportUserIds = prepareSupportUserIds(supportState);
      const products = requestData?.products
          .filter((p) => editSelectedProductIds.includes(p.productId))
          .map(({ id, ...rest }) => rest);

      const updatedPlan = await createExitPlan({
        planId: activePlanId,
        exitRequestResponseId: requestData.id,
        additionalComments,
        exitDate,
        replacementPlan,
        endOfProductionDate,
        endOfImportationDate,
        liquidationPlan,
        planType,
        toImpactEuros,
        exceptionStartDate,
        exceptionEndDate,
        justification,
        safetyAssessmentReference,
        productTypes,
        brands,
        marketingSupport: supportState.marketingSupport,
        supplyChainSupport: supportState.supplyChainSupport,
        supportUserIds,
        products,
        updating: true
      });

      // Use backend data for updated plan
      setCreatedPlans((prev) =>
          prev.map((plan) =>
              plan.id === updatedPlan.id
                  ? {
                    ...plan,
                    productIds: editSelectedProductIds,
                    planType: mapBackendPlanToFrontend(updatedPlan, 0).planType,
                    formData: mapBackendPlanToFrontend(updatedPlan, 0).formData
                  }
                  : plan
          )
      );

      setPlannedProductIds([
        ...new Set([
          ...createdPlans
              .filter((plan) => plan.id !== updatedPlan.id)
              .flatMap((plan) => plan.productIds),
          ...editSelectedProductIds
        ])
      ]);

      setActivePlanId(updatedPlan.id);
      setIsPlanDirty(false);
      setEditLeftTab(1);
      setPlanViewMode(true);
    } catch (e) {
      dispatch(setSnackbar({ open: true, message: "Error updating exit plan.", error: true }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePlan = async () => {
    if (!activePlanId) return;
    const plan = getPlanById(activePlanId);
    if (!plan) return;

    setDeleteLoading(true);
    try {
      await deleteExitPlan(activePlanId);

      setPlannedProductIds((prev) =>
          prev.filter((id) => !plan.productIds.includes(id))
      );
      setCreatedPlans((prev) => prev.filter((p) => p.id !== activePlanId));

      handleAddAnotherPlan();
      setTabValue(1);
    } catch (e) {
      // Error handled in hook
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleDeletePlanConfirmed = () => handleDeletePlan();

  // Approval/Reject handler
  const handleApprovalClick = (type) => {
    setApprovalType(type);
    setApprovalDialogOpen(true);
    setApprovalComment("");
  };

  const handleApprovalConfirm = async () => {
    setApprovalLoading(true);
    try {
      await axiosInstance.post(
        `/exit-plans/${activePlanId}/approve`,
        {
          approved: approvalType === "approve",
          comment: approvalComment
        }
      );
      setApprovalDialogOpen(false);
      // Preserve selected plan and open Plan Status tab (viewTabValue = 2)
      window.location.replace(
        `${window.location.pathname}?showSnackbar=planApproved&selectedPlanId=${activePlanId}&openPlanStatusTab=1`
      );
    } catch (err) {
      console.log("Plan approval error:", err);
      dispatch(setSnackbar({
        open: true,
        message: "Failed to process approval/rejection.",
        error: true
      }));
      setApprovalDialogOpen(false);
    } finally {
      setApprovalLoading(false);
    }
  };

  // ================ Effects ================
  // Load plans from backend data
  useEffect(() => {
    if (requestData && Array.isArray(requestData.exitPlans) && requestData.exitPlans.length > 0) {
      const plans = requestData.exitPlans.map(mapBackendPlanToFrontend);
      setCreatedPlans(plans);
      const allPlannedProductIds = plans.flatMap(p => p.productIds);
      setPlannedProductIds(allPlannedProductIds);
    }
  }, [requestData]);

  // Reset form when switching between plans
  useEffect(() => {
    if (activePlanId && planFormSnapshot && planProductIdsSnapshot) {
      setExitDate(planFormSnapshot.exitDate || null);
      setEndOfProductionDate(planFormSnapshot.endOfProductionDate || null);
      setEndOfImportationDate(planFormSnapshot.endOfImportationDate || null);
      setReplacementPlan(planFormSnapshot.replacementPlan || '');
      setLiquidationPlan(planFormSnapshot.liquidationPlan || '');
      setAdditionalComments(planFormSnapshot.additionalComments || '');
      setPlanType(normalizePlanType(planFormSnapshot.planType));
      setToImpactEuros(planFormSnapshot.toImpactEuros ?? EMPTY_EXEMPTION_FIELDS.toImpactEuros);
      setExceptionStartDate(planFormSnapshot.exceptionStartDate || null);
      setExceptionEndDate(planFormSnapshot.exceptionEndDate || null);
      setJustification(planFormSnapshot.justification || '');
      setSafetyAssessmentReference(planFormSnapshot.safetyAssessmentReference || '');
      setProductTypes(planFormSnapshot.productTypes || '');
      setBrands(planFormSnapshot.brands || '');
      setFormErrors({});
      setEditSelectedProductIds(planProductIdsSnapshot);
      setSupportState({
        needsSupport: planFormSnapshot.needsSupport || false,
        marketingSupport: planFormSnapshot.marketingSupport || false,
        supplyChainSupport: planFormSnapshot.supplyChainSupport || false,
        selectedMarketingMember: planFormSnapshot.selectedMarketingMember || null,
        selectedSupplyChainMember: planFormSnapshot.selectedSupplyChainMember || null
      });
    }
  }, [activePlanId]);

  // Check if plan is dirty
  useEffect(() => {
    if (activePlanId) {
      const plan = getPlanById(activePlanId);
      if (!plan) return;

      const d = plan.formData;
      const currentIds = [...(editSelectedProductIds || [])].sort();
      const snapshotIds = [...(planProductIdsSnapshot || [])].sort();
      const productIdsChanged =
          currentIds.length !== snapshotIds.length ||
          currentIds.some((id, idx) => id !== snapshotIds[idx]);

      const supportChanged =
          d.needsSupport !== supportState.needsSupport ||
          d.marketingSupport !== supportState.marketingSupport ||
          d.supplyChainSupport !== supportState.supplyChainSupport ||
          d.selectedMarketingMember !== supportState.selectedMarketingMember ||
          d.selectedSupplyChainMember !== supportState.selectedSupplyChainMember;

      setIsPlanDirty(
          d.planType !== planType ||
          String(d.toImpactEuros ?? '') !== String(toImpactEuros ?? '') ||
          d.exceptionStartDate !== exceptionStartDate ||
          d.exceptionEndDate !== exceptionEndDate ||
          d.justification !== justification ||
          d.safetyAssessmentReference !== safetyAssessmentReference ||
          d.productTypes !== productTypes ||
          d.brands !== brands ||
          d.exitDate !== exitDate ||
          d.endOfProductionDate !== endOfProductionDate ||
          d.endOfImportationDate !== endOfImportationDate ||
          d.replacementPlan !== replacementPlan ||
          d.liquidationPlan !== liquidationPlan ||
          d.additionalComments !== additionalComments ||
          productIdsChanged ||
          supportChanged
      );
    }
  }, [
    activePlanId,
    exitDate,
    endOfProductionDate,
    endOfImportationDate,
    replacementPlan,
    liquidationPlan,
    additionalComments,
    planType,
    toImpactEuros,
    exceptionStartDate,
    exceptionEndDate,
    justification,
    safetyAssessmentReference,
    productTypes,
    brands,
    editSelectedProductIds,
    planProductIdsSnapshot,
    supportState
  ]);

  // Add these constants and helper function near the top of the component
  const PLAN_VIEW_ONLY_STATUSES = ['SUBMITTED', 'UNDER_REVIEW', 'APPROVED', 'PENDING_LOCAL_RISK_ASSESSMENT'];
  const PLAN_SUBMIT_EXCLUDED_STATUSES = ['WAITING_FOR_SUPPORT', 'APPROVED', 'SUBMITTED', 'REJECTED', 'REVISION_REQUIRED', 'PENDING_LOCAL_RISK_ASSESSMENT'];
  const isImpactAnalysisDraft = !requestData?.status || (requestData?.status || '').toUpperCase() === 'DRAFT';

  const getActivePlanStatus = () => {
    const backendData = getActivePlanBackendData();
    return backendData?.status;
  };

  const getShowEditButton = () => {
     if(activePlanId){
         const status = getActivePlanStatus();
         console.log(status);
         console.log(PLAN_VIEW_ONLY_STATUSES)
         return !PLAN_VIEW_ONLY_STATUSES.includes(status) ;
     }
  }

  // Remove edit mode logic and always show view mode for SUBMITTED/UNDER_REVIEW
  useEffect(() => {
    if (activePlanId) {
      const status = getActivePlanStatus();
      if (PLAN_VIEW_ONLY_STATUSES.includes(status)) {
        setPlanViewMode(true);
      }
    }
  }, [activePlanId, createdPlans]);

  // Handle loading state
  useEffect(() => {
    if (!loading) {
      setShowLoading(false);
    }
  }, [loading]);

  // Open plan from location state, query param, or default to first plan if available
  useEffect(() => {
    // Check for selectedPlanId in query params (for reload after submit/approve)
    const params = new URLSearchParams(window.location.search);
    const selectedPlanId = params.get("selectedPlanId");

    // If selectedPlanId is present in query params, use it
    if (selectedPlanId && createdPlans.length > 0) {
      const found = createdPlans.find((p) => p.id === Number(selectedPlanId));
      if (found) {
        setActivePlanId(found.id);
        setPlanViewMode(true);
        initializeFormFromPlan(found);
        return;
      }
    }

    // If a planId is passed in location.state, open that plan
    if (location.state?.planId && createdPlans.length > 0) {
      const found = createdPlans.find((p) => p.id === location.state.planId);
      if (found) {
        setActivePlanId(found.id);
        setPlanViewMode(true);
        initializeFormFromPlan(found);
        return;
      }
    }

    // If no planId is passed and there are plans, open the first plan by id
    if (createdPlans.length > 0 && !activePlanId) {
      const sortedPlans = [...createdPlans].sort((a, b) => a.id - b.id);
      setActivePlanId(sortedPlans[0].id);
      setPlanViewMode(true);
      initializeFormFromPlan(sortedPlans[0]);
    }
  }, [createdPlans]);

  // Check for unplanned products after plan creation
  useEffect(() => {
    if (planJustCreated) {
      if (getUnplannedProducts().length > 0) {
        setShowUnplannedProductsDialog(true);
      }
      setPlanJustCreated(false);
    }
  }, [plannedProductIds, createdPlans]);

  // On mount, check for query params and show snackbar if needed, and restore selected plan/tab
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const showSnackbar = params.get("showSnackbar");
    const selectedPlanId = params.get("selectedPlanId");
    const openPlanStatusTab = params.get("openPlanStatusTab");
    console.log("selectedPlanId: ", selectedPlanId);
    if (selectedPlanId && createdPlans.length > 0) {
      const found = createdPlans.find(p => p.id == selectedPlanId);
        console.log("found plan: ", found);
      if (found) {
        console.log(found);
        setActivePlanId(found.id);
        setPlanViewMode(true);
        initializeFormFromPlan(found);
      }
    }
    if (openPlanStatusTab === "1") {
      setViewTabValue(2); // Plan Status tab index
    }
    if (showSnackbar === "planApproved") {
      dispatch(setSnackbar({
        open: true,
        message: approvalType === "approve"
          ? "Changes applied successfully."
          : "Changes applied successfully.",
        error: false
      }));
      params.delete("showSnackbar");
      params.delete("selectedPlanId");
      params.delete("openPlanStatusTab");
      window.history.replaceState({}, '', window.location.pathname + (params.toString() ? '?' + params.toString() : ''));
    }
    // Show snackbar for plan submission
    if (showSnackbar === "planSubmitted") {
      dispatch(setSnackbar({
        open: true,
        message: "Submitted successfully",
        error: false
      }));
      params.delete("showSnackbar");
      params.delete("selectedPlanId");
      params.delete("openPlanStatusTab");
      window.history.replaceState({}, '', window.location.pathname + (params.toString() ? '?' + params.toString() : ''));
    }
  }, [createdPlans, dispatch]);

  // ================ Computed Values ================
  const productIdsForDisplay = activePlanId ? editSelectedProductIds : selectedProductIds;

  // ================ Conditional Rendering ================
  if ((loading && showLoading) || (isEditing && !requestData)) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <LoadingComponent size="5em" thickness={5} color="primary" />
        </Box>
    );
  }

  const showSupportTab = (!!activePlanId) || selectedProductIds.length > 0;
  const supportTabIndex = activePlanId ? 2 : 2;

  // ================ Render Functions ================
  const renderSupportTabContent = () => (
      <StyledCard sx={{ ...cardTransition(theme), flexShrink: 0 }}>
        <CardContent sx={{ ...cardContentStyles }}>
          <SectionTitle variant="h6" sx={sectionTitleStyles(theme)}>
            {UI_TEXT.SUPPORT_REQUEST}
          </SectionTitle>
          <SupportRequestTab
              needsSupport={supportState.needsSupport}
              marketingSupport={supportState.marketingSupport}
              supplyChainSupport={supportState.supplyChainSupport}
              selectedMarketingMember={supportState.selectedMarketingMember}
              selectedSupplyChainMember={supportState.selectedSupplyChainMember}
              setNeedsSupport={setPlanNeedsSupport}
              setMarketingSupport={setPlanMarketingSupport}
              setSupplyChainSupport={setPlanSupplyChainSupport}
              setSelectedMarketingMember={setPlanSelectedMarketingMember}
              setSelectedSupplyChainMember={setPlanSelectedSupplyChainMember}
              formErrors={formErrors}
              theme={theme}
          />
        </CardContent>
      </StyledCard>
  );

  const renderLeftColumn = () => {
    if (activePlanId) {
      return planViewMode ? (
          // View mode tabs
          <>
            <Tabs
                value={viewTabValue}
                onChange={(_, v) => setViewTabValue(v)}
                sx={tabsStyles(theme)}
                variant="fullWidth"
            >
              <Tab label={TAB_LABELS.REQUEST_DETAILS} />
              <Tab label={TAB_LABELS.PRODUCTS} />
              <Tab label={TAB_LABELS.PLAN_STATUS} />
              <Tab label={TAB_LABELS.SUPPORT_DETAILS} />
            </Tabs>
            <CardContent sx={{ ...cardContentStyles, display: 'flex', flexDirection: 'column', p: 2, flex: 1, overflow: 'auto' }}>
              {viewTabValue === VIEW_TAB_INDICES.REQUEST_DETAILS && (
                  <RequestDetailsTab requestData={requestData} theme={theme} />
              )}
              {viewTabValue === VIEW_TAB_INDICES.PRODUCTS && (
                  <>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {UI_TEXT.PRODUCTS_IN_PLAN}
                    </Typography>
                    <AffectedProductsTab
                        affectedProducts={requestData?.products.filter((p) => editSelectedProductIds.includes(p.productId))}
                        theme={theme}
                        readOnly={true}
                    />
                  </>
              )}
              {viewTabValue === VIEW_TAB_INDICES.PLAN_STATUS && (
                  <PlanStatusTab
                      createdAt={getActivePlanBackendData()?.createdAt}
                      lastUpdatedAt={getActivePlanBackendData()?.lastUpdatedAt}
                      status={getActivePlanBackendData()?.status}
                      approved={getActivePlanBackendData()?.approved}
                      approvedBy={getActivePlanBackendData()?.approvedBy}
                      approvalDate={getActivePlanBackendData()?.approvalDate}
                      approvalComment={getActivePlanBackendData()?.approvalComment}
                      approvals={getActivePlanBackendData()?.approvals || []}
                      approvingManagers={getActivePlanBackendData()?.approvingManagers || []}
                      totalApprovers={getActivePlanBackendData()?.totalApprovers || 0}
                      approvedCount={getActivePlanBackendData()?.approvedCount || 0}
                      planType={getActivePlanBackendData()?.planType}
                      localRiskAssessment={getActivePlanBackendData()?.localRiskAssessment}
                      localRiskAssessmentBy={getActivePlanBackendData()?.localRiskAssessmentBy}
                      localRiskAssessmentDate={getActivePlanBackendData()?.localRiskAssessmentDate}
                      theme={theme}
                  />
              )}
              {viewTabValue === VIEW_TAB_INDICES.SUPPORT_DETAILS && (
                  <>
                    {/* New Support Requests Section */}
                    <SupportTab
                        exitPlanId={activePlanId}
                        isEditable={!isReadOnly}
                        disabled={isReadOnly}
                        planStatus={getActivePlanBackendData()?.status}
                    />
                    
                    {/* Legacy Support Details Section */}
                    {(getActivePlanBackendData()?.marketingSupport || getActivePlanBackendData()?.supplyChainSupport) && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                          Legacy Support Details
                        </Typography>
                        <SupportDetailsTab
                            needsSupport={getActivePlanBackendData()?.marketingSupport || getActivePlanBackendData()?.supplyChainSupport}
                            marketingSupport={getActivePlanBackendData()?.marketingSupport}
                            supplyChainSupport={getActivePlanBackendData()?.supplyChainSupport}
                            marketingSupportProvided={getActivePlanBackendData()?.marketingSupportProvided}
                            supplyChainSupportProvided={getActivePlanBackendData()?.supplyChainSupportProvided}
                            selectedMarketingMember={
                              Array.isArray(getActivePlanBackendData()?.supportUsers)
                                  ? getActivePlanBackendData().supportUsers.find(u => u.role === USER_ROLES.MARKETING)
                                  : null
                            }
                            selectedSupplyChainMember={
                              Array.isArray(getActivePlanBackendData()?.supportUsers)
                                  ? getActivePlanBackendData().supportUsers.find(u => u.role === USER_ROLES.SUPPLY_CHAIN)
                                  : null
                            }
                            comments={getActivePlanBackendData()?.comments}
                            reviewStatus={getActivePlanBackendData()?.status}
                            reviewComments={getActivePlanBackendData()?.approvalComment}
                            theme={theme}
                        />
                      </Box>
                    )}
                  </>
              )}

            </CardContent>
          </>
      ) : (
          // Edit mode tabs
          <>
            <Tabs
                value={editLeftTab}
                onChange={(_, v) => setEditLeftTab(v)}
                sx={tabsStyles(theme)}
                variant="fullWidth"
            >
              <Tab label={TAB_LABELS.REQUEST_DETAILS} />
              <Tab label={TAB_LABELS.PRODUCTS} />
              <Tab label={TAB_LABELS.SUPPORT} />
            </Tabs>
            <CardContent sx={{ ...cardContentStyles, display: 'flex', flexDirection: 'column', p: 2, flex: 1, overflow: 'auto' }}>
              {editLeftTab === 0 && (
                  <RequestDetailsTab requestData={requestData} theme={theme} />
              )}
              {editLeftTab === 1 && (
                  <>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {UI_TEXT.SELECT_PRODUCTS_FOR_PLAN}
                    </Typography>
                    <AffectedProductsTab
                        affectedProducts={getEditablePlanProducts()}
                        theme={theme}
                        selectedProductIds={editSelectedProductIds}
                        onSelectProduct={(productId) => {
                          setEditSelectedProductIds((prev) =>
                              prev.includes(productId)
                                  ? prev.filter((id) => id !== productId)
                                  : [...prev, productId]
                          );
                        }}
                        plannedProductIds={[]}
                        readOnly={false}
                    />
                  </>
              )}
              {editLeftTab === 2 && (
                  <>
                    {/* New Support Requests Section */}
                    <SupportTab
                        exitPlanId={activePlanId}
                        isEditable={!isReadOnly}
                        disabled={isReadOnly}
                        planStatus={getActivePlanBackendData()?.status}
                    />
                    
                    {/* Legacy Support Request Section */}
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        {UI_TEXT.SUPPORT_REQUEST} (Legacy)
                      </Typography>
                      <SupportRequestTab
                          needsSupport={supportState.needsSupport}
                          marketingSupport={supportState.marketingSupport}
                          supplyChainSupport={supportState.supplyChainSupport}
                          selectedMarketingMember={supportState.selectedMarketingMember}
                          selectedSupplyChainMember={supportState.selectedSupplyChainMember}
                          setNeedsSupport={setPlanNeedsSupport}
                          setMarketingSupport={setPlanMarketingSupport}
                          setSupplyChainSupport={setPlanSupplyChainSupport}
                          setSelectedMarketingMember={setPlanSelectedMarketingMember}
                          setSelectedSupplyChainMember={setPlanSelectedSupplyChainMember}
                          formErrors={formErrors}
                          theme={theme}
                      />
                    </Box>
                  </>
              )}
            </CardContent>
          </>
      );
    }

    // Create mode tabs
    return (
        <>
          <Tabs
              value={tabValue}
              onChange={handleTabChange}
              sx={tabsStyles(theme)}
              variant="fullWidth"
          >
            <Tab label={TAB_LABELS.REQUEST_DETAILS} />
            <Tab label={TAB_LABELS.PRODUCTS} />
            {showSupportTab && <Tab label={TAB_LABELS.SUPPORT} />}
          </Tabs>
          <Box sx={tabContentStyles}>
            {tabValue === TAB_INDICES.REQUEST_DETAILS && (
                <RequestDetailsTab requestData={requestData} theme={theme} />
            )}
            {tabValue === TAB_INDICES.PRODUCTS && (
                <StyledCard sx={{ ...cardTransition(theme), flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ ...cardContentStyles, display: 'flex', flexDirection: 'column', p: 2 }}>
                    <AffectedProductsTab
                        affectedProducts={getUnplannedProducts()}
                        theme={theme}
                        selectedProductIds={selectedProductIds}
                        onSelectProduct={handleSelectProduct}
                        plannedProductIds={plannedProductIds}
                    />
                  </CardContent>
                </StyledCard>
            )}
            {showSupportTab && tabValue === supportTabIndex && renderSupportTabContent()}
          </Box>
        </>
    );
  };

  // Confirmation dialog for submit
  const SubmitConfirmationDialog = (
    <Dialog
      open={submitDialogOpen}
      onClose={() => setSubmitDialogOpen(false)}
      PaperProps={{
        sx: {
          minWidth: 600,
          maxWidth: 800,
        }
      }}
    >
      <DialogTitle>{isExemption(planType) ? 'Submit Exemption Request?' : 'Submit Exit Plan?'}</DialogTitle>
      <DialogContent>
        <Typography>
          {isExemption(planType)
            ? 'The exemption request will be sent to the regulatory owner for the Local Regulatory Risk Assessment and then to the approving managers. You will not be able to edit it unless it is rejected.'
            : 'Exit plans are final once submitted. You will no longer be able to edit this plan. Are you sure you want to submit?'}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setSubmitDialogOpen(false)} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={async () => {
            await submitExitPlan(activePlanId);
            setSubmitDialogOpen(false);
          }}
          color="primary"
          variant="contained"
          disabled={submitLoading}
        >
          {submitLoading ? "Submitting..." : "Yes, Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Approval Confirmation Dialog
  const ApprovalConfirmationDialog = (
    <Dialog
      open={approvalDialogOpen}
      onClose={() => setApprovalDialogOpen(false)}
      PaperProps={{
        sx: {
          minWidth: 600,
          maxWidth: 800,
        }
      }}
      BackdropProps={{
        style: { backgroundColor: "rgba(0,0,0,0.2)" }
      }}
    >
      <DialogTitle>
        {approvalType === "approve" ? "Approve Exemption Request" : "Reject Exemption Request"}
      </DialogTitle>
      <DialogContent>
        <Typography gutterBottom>
          {approvalType === "approve"
            ? "Are you sure you want to approve this submitted exemption request?"
            : "Are you sure you want to reject this submitted exemption request?"}
        </Typography>
        <TextField
          label="Comment (optional)"
          fullWidth
          multiline
          minRows={6}
          value={approvalComment}
          onChange={(e) => setApprovalComment(e.target.value)}
          sx={{ mt: 2, minHeight: 180 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setApprovalDialogOpen(false)} disabled={approvalLoading}>Cancel</Button>
        <Button
          variant="contained"
          color={approvalType === "approve" ? "success" : "error"}
          onClick={handleApprovalConfirm}
          disabled={approvalLoading}
        >
          {approvalLoading
            ? (approvalType === "approve" ? "Approving..." : "Rejecting...")
            : (approvalType === "approve" ? "Confirm Approval" : "Confirm Rejection")}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ================ Main Render ================
  return (
      <PageContainer>
        {/* Header Section */}
        <HeaderContainer sx={{ position: 'relative' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <PageTitle
                title={UI_TEXT.PAGE_TITLE}
                PageIcon={<EditCalendarRoundedIcon color="white" fontSize="large" />}
                iconColor={theme.palette.primary.main}
                noMargin
            />
          </Box>

          {/* Plan Chips */}
          <PlanChipsSection
              createdPlans={createdPlans}
              activePlanId={activePlanId}
              onPlanClick={handlePlanNameClick}
              theme={theme}
          />

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: 2 }}>
            {activePlanId && !isReadOnly && (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleAddAnotherPlan}
                    sx={{ textTransform: 'none' }}
                >
                  {UI_TEXT.ADD_PLAN}
                </Button>
            )}
            <Button
                variant="outlined"
                size="small"
                onClick={handleBack}
                sx={{ textTransform: 'none' }}
            >
              {UI_TEXT.BACK}
            </Button>
          </Box>
        </HeaderContainer>

        {/* Main Content */}
        <Box sx={contentContainerStyles}>
          {/* Left Column */}
          <Box sx={leftColumnStyles}>
            {renderLeftColumn()}
          </Box>

          {/* Right Column */}
          <Box sx={rightColumnStyles}>
            <StyledCard sx={{ ...cardTransition(theme), height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {/* Sticky Title with Edit/View Toggle */}
              <Box sx={stickyTitleStyles(theme)}>
                <SectionTitle variant="h6" sx={sectionTitleStyles(theme)}>
                  {UI_TEXT.EXIT_PLAN_DETAILS}
                </SectionTitle>
                {activePlanId && getShowEditButton() && !isReadOnly && (
                    <FormControlLabel
                        control={
                          <ModeSwitch
                              checked={!planViewMode}
                              onChange={() => setPlanViewMode((v) => !v)}
                              color="primary"
                              sx={{ mr: 1 }}
                          />
                        }
                        label={!planViewMode ? UI_TEXT.VIEW : UI_TEXT.EDIT}
                        sx={{ userSelect: 'none', ml: 1 }}
                    />
                )}
              </Box>

              <Box
                  sx={{
                    flex: 1,
                    overflow: 'auto',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column'
                  }}
              >
                <Box sx={{ px: 1 }}>
                  <PlanProductsDisplay
                      products={requestData?.products || []}
                      selectedProductIds={productIdsForDisplay}
                      isViewMode={activePlanId ? planViewMode : true}
                      onRemoveProduct={activePlanId ? handleRemoveProductFromPlan : handleSelectProduct}
                  />
                </Box>

                <CardContent
                    sx={{
                      ...rightCardContentStyles(cardContentStyles),
                      flex: 1,
                      overflow: 'visible',
                      minHeight: 0,
                      pt: 0.5,
                      pb: 0,
                      pl: 1,
                      pr: 1,
                      position: "relative"
                    }}
                >
                {(!activePlanId && selectedProductIds.length === 0) ? (
                    <Box sx={emptyStateStyles(theme)}>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {UI_TEXT.SELECT_PRODUCTS_MESSAGE}
                      </Typography>
                    </Box>
                ) : (
                    activePlanId && planViewMode ? (
                        <>
                        <PlanViewContent
                            planDetails={getActivePlanDetails()}
                            restrictionType={requestData?.exitRequest?.restrictionType}
                            theme={theme}
                        />
                          {/* Add Submit Plan button in view mode if status is not excluded */}
                          {(() => {
                            const status = getActivePlanStatus();
                            if (status && !PLAN_SUBMIT_EXCLUDED_STATUSES.includes(status) && !isReadOnly) {
                              return (
                                <>
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    sx={{ mt: 2 }}
                                    disabled={submitLoading || isImpactAnalysisDraft}
                                    onClick={() => setSubmitDialogOpen(true)}
                                  >
                                    {submitLoading ? "Submitting..." : (isExemption(planType) ? "Submit Exemption Request" : "Submit Plan")}
                                  </Button>
                                  {isImpactAnalysisDraft && (
                                    <Alert severity="warning" sx={{ mt: 1 }}>
                                      Plan cannot be submitted until the exit request response is submitted.
                                    </Alert>
                                  )}
                                  {SubmitConfirmationDialog}
                                </>
                              );
                            }
                            return null;
                          })()}
                          {/* Approve/Reject buttons for approving managers in readonly mode and status is SUBMITTED */}
                          {(() => {
                            const status = getActivePlanStatus();
                            const planData = getActivePlanBackendData();
                            const hasAlreadyApproved = planData?.approvals?.some(
                              a => a.approver?.id === userId
                            );
                            if (
                              isReadOnly &&
                              isApprovingManager &&
                              status === "SUBMITTED" &&
                              isExemption(planData) &&
                              !hasAlreadyApproved
                            ) {
                              return (
                                <Box sx={{
                                  position: "absolute",
                                  bottom: 16,
                                  right: 16,
                                  display: "flex",
                                  gap: 2,
                                  zIndex: 10
                                }}>
                                  <Button
                                    variant="outlined"
                                    color="warning"
                                    onClick={() => setRequestApproverOpen(true)}
                                  >
                                    Add Approver
                                  </Button>
                                  <Button
                                    variant="contained"
                                    color="success"
                                    onClick={() => handleApprovalClick("approve")}
                                  >
                                    Approve Exemption
                                  </Button>
                                  <Button
                                    variant="contained"
                                    color="error"
                                    onClick={() => handleApprovalClick("reject")}
                                  >
                                    Reject Exemption
                                  </Button>
                                  {ApprovalConfirmationDialog}
                                </Box>
                              );
                            }
                            if (
                              isReadOnly &&
                              isApprovingManager &&
                              status === "SUBMITTED" &&
                              isExemption(planData) &&
                              hasAlreadyApproved
                            ) {
                              return (
                                <Box sx={{
                                  position: "absolute",
                                  bottom: 16,
                                  right: 16,
                                  zIndex: 10
                                }}>
                                  <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
                                    You have already submitted your approval for this plan.
                                  </Alert>
                                </Box>
                              );
                            }
                            return null;
                          })()}
                        </>
                    ) : (
                        <ExitPlanFormSection
                            requestData={requestData}
                            formErrors={formErrors}
                            exitDate={exitDate}
                            endOfProductionDate={endOfProductionDate}
                            endOfImportationDate={endOfImportationDate}
                            replacementPlan={replacementPlan}
                            liquidationPlan={liquidationPlan}
                            additionalComments={additionalComments}
                            needsSupport={supportState.needsSupport}
                            setExitDate={setExitDate}
                            setEndOfProductionDate={setEndOfProductionDate}
                            setEndOfImportationDate={setEndOfImportationDate}
                            setReplacementPlan={setReplacementPlan}
                            setLiquidationPlan={setLiquidationPlan}
                            setAdditionalComments={setAdditionalComments}
                            planType={planType}
                            onPlanTypeChange={setPlanType}
                            allowTypeSwitch={true}
                            toImpactEuros={toImpactEuros}
                            exceptionStartDate={exceptionStartDate}
                            exceptionEndDate={exceptionEndDate}
                            justification={justification}
                            safetyAssessmentReference={safetyAssessmentReference}
                            productTypes={productTypes}
                            brands={brands}
                            setToImpactEuros={setToImpactEuros}
                            setExceptionStartDate={setExceptionStartDate}
                            setExceptionEndDate={setExceptionEndDate}
                            setJustification={setJustification}
                            setSafetyAssessmentReference={setSafetyAssessmentReference}
                            setProductTypes={setProductTypes}
                            setBrands={setBrands}
                            handleSubmit={activePlanId ? handleUpdatePlan : handleCreatePlan}
                            isEditing={!!activePlanId}
                            submitting={submitting}
                            theme={theme}
                            disableSubmit={
                              activePlanId
                                  ? (!isPlanDirty || !isSupportValid(supportState) || getActivePlanProducts().length === 0)
                                  : (selectedProductIds.length === 0 || !isSupportValid(supportState) || isSupportTeamSelectedButNoMember(supportState))
                            }
                            showDelete={!!activePlanId && !planViewMode}
                            onDelete={() => setDeleteDialogOpen(true)}
                            showSupportAlert={showSupportTab && !supportState.needsSupport}
                        />
                    )
                )}
                </CardContent>
              </Box>
            </StyledCard>
          </Box>
        </Box>

        {/* Delete Confirmation Dialog */}
        {DeletePopup(
            "plan",
            deleteDialogOpen,
            () => setDeleteDialogOpen(false),
            handleDeletePlanConfirmed,
            null,
            deleteLoading,
            "delete"
        )}

        {/* Reminder Dialog */}
        <Dialog open={showUnplannedProductsDialog} onClose={() => setShowUnplannedProductsDialog(false)}>
          <DialogTitle>Reminder</DialogTitle>
          <DialogContent>
            <Typography>
              Some impacted products are not included in any exit plan or exemption request. Please cover all impacted products.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowUnplannedProductsDialog(false)} color="primary">OK</Button>
          </DialogActions>
        </Dialog>

        {/* Approval Confirmation Dialog */}
        {ApprovalConfirmationDialog}

        {/* Add Approver Dialog */}
        <RequestApproverDialog
          open={requestApproverOpen}
          onClose={() => setRequestApproverOpen(false)}
          exitRequestId={requestData?.exitRequest?.id}
          onSaved={() => {
            setRequestApproverOpen(false);
            dispatch(setSnackbar({ open: true, message: 'Approver added successfully.', error: false }));
          }}
        />
        {extensionDialogOpen && (
          <ExtensionRequestDialog
            open={extensionDialogOpen}
            onClose={() => setExtensionDialogOpen(false)}
            exitRequestId={Number(activeExitRequestId)}
            categoryAssignmentId={Number(activeCategoryAssignmentId)}
            onSubmitted={() =>
              dispatch(setSnackbar({
                open: true,
                message: "Extension request submitted.",
                error: false,
              }))
            }
          />
        )}
      </PageContainer>
  );
};

export default ExitPlanPage;
